import { getConfig } from "../config";
import { getAccessToken, redirectToSignIn } from "../auth/tokenHelper";
import { ApiError } from "../types";

const DEFAULT_TIMEOUT_MS = 30_000;

export interface ApiFetchOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: BodyInit | null;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  timeoutMs?: number;
  /** Statuses the caller handles itself; the thrown ApiError is flagged so the interaction can skip the toast. */
  allowedStatuses?: readonly number[];
}

function mergeSignals(callerSignal: AbortSignal | undefined, timeoutSignal: AbortSignal): AbortSignal {
  if (!callerSignal) return timeoutSignal;
  return AbortSignal.any([callerSignal, timeoutSignal]);
}

async function readErrorBody(response: Response): Promise<{ message: string; body: unknown }> {
  let body: unknown;
  let message = `API error: ${response.status}`;
  try {
    body = await response.json();
    const record = body as { message?: unknown; error?: unknown; title?: unknown; detail?: unknown };
    if (typeof record?.detail === "string") message = record.detail;
    else if (typeof record?.message === "string") message = record.message;
    else if (typeof record?.error === "string") message = record.error;
    else if (typeof record?.title === "string") message = record.title;
  } catch {
    body = undefined;
  }
  return { message, body };
}

/**
 * Thin fetch wrapper: base URL, bearer token, JSON headers, 30s timeout, error
 * normalization. It never publishes to the event bus; the interaction that owns
 * the call decides whether the failure is user-facing.
 */
export async function apiFetch<T>(path: string, options: ApiFetchOptions = {}): Promise<T> {
  const config = getConfig();
  const timeoutController = new AbortController();
  const timeoutId = setTimeout(() => timeoutController.abort(), options.timeoutMs ?? DEFAULT_TIMEOUT_MS);
  const token = await getAccessToken();
  const headers: Record<string, string> = {
    ...(options.body instanceof FormData ? {} : { "Content-Type": "application/json" }),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(!config.AUTH_ENABLED && config.DEV_ACTOR_EMAIL ? { "X-Actor-Id": config.DEV_ACTOR_EMAIL } : {}),
    ...options.headers,
  };

  try {
    const response = await fetch(`${config.API_BASE_URL}${path}`, {
      method: options.method ?? "GET",
      body: options.body,
      headers,
      signal: mergeSignals(options.signal, timeoutController.signal),
    });

    if (response.status === 401 && config.AUTH_ENABLED) {
      await redirectToSignIn();
      throw new ApiError("Session expired. Redirecting to sign in.", 401);
    }
    if (!response.ok) {
      const { message, body } = await readErrorBody(response);
      const error = new ApiError(message, response.status, body);
      error.isExpected = options.allowedStatuses?.includes(response.status) ?? false;
      throw error;
    }
    if (response.status === 204 || response.headers.get("content-length") === "0") return undefined as T;
    return (await response.json()) as T;
  } catch (error) {
    if (error instanceof ApiError) throw error;
    if (error instanceof Error && error.name === "AbortError") {
      throw new ApiError(options.signal?.aborted ? "Request cancelled" : "Request timed out", 408);
    }
    throw error instanceof Error ? error : new Error(String(error));
  } finally {
    clearTimeout(timeoutId);
  }
}

export function buildQuery(params: Record<string, string | number | boolean | null | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    search.set(key, String(value));
  }
  const text = search.toString();
  return text === "" ? "" : `?${text}`;
}
